import { useEffect, useRef } from 'react';

import mapboxgl from 'mapbox-gl';

import arrowIcon from '../../icons/arrow.svg';
import { MAP_CONSTANTS } from './mapConstants';
import { updatePathSource, flyToPosition, jumpToPosition } from './mapOperations';
import { getMidpoint, adjustBearingForMapRotation } from './mapUtils';

export function useTelemetryMarker(
  map: React.RefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  currentPosition: [number, number] | null,
  prevPosition: [number, number] | null,
  firstDataReceived: boolean,
  headingDeg: number | undefined,
  cumulativePath: [number, number][],
  targetZoomRef: React.MutableRefObject<number>,
  createdTimeRef: React.MutableRefObject<number>,
  inPipMode: boolean,
  flyToEnabledRef: React.MutableRefObject<boolean>
) {
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const hasInitiallyPositionedRef = useRef<boolean>(false);

  useEffect(() => {
    hasInitiallyPositionedRef.current = false;
  }, [mapLoaded, inPipMode]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !currentPosition) return;

    if (!markerRef.current && firstDataReceived) {
      const arrowElement = document.createElement('img');
      arrowElement.src = arrowIcon;
      arrowElement.style.width = `${MAP_CONSTANTS.ARROW_MARKER_SIZE}px`;
      arrowElement.style.height = `${MAP_CONSTANTS.ARROW_MARKER_SIZE}px`;

      markerRef.current = new mapboxgl.Marker({ element: arrowElement }).setLngLat(currentPosition).addTo(map.current);
    }

    if (markerRef.current && prevPosition && headingDeg !== undefined) {
      const midpoint = getMidpoint(prevPosition, currentPosition);
      const mapBearing = map.current.getBearing();
      const adjustedBearing = adjustBearingForMapRotation(headingDeg, mapBearing);

      markerRef.current.setLngLat(midpoint);
      markerRef.current.setRotation(adjustedBearing);

      if (inPipMode && !hasInitiallyPositionedRef.current) {
        jumpToPosition(map.current, currentPosition, targetZoomRef, createdTimeRef, targetZoomRef.current);
        hasInitiallyPositionedRef.current = true;
      } else {
        flyToPosition(
          map.current,
          currentPosition,
          targetZoomRef,
          createdTimeRef,
          targetZoomRef.current,
          flyToEnabledRef.current
        );
      }

      const displayedCoordinates = [...cumulativePath.slice(0, -1), midpoint];
      updatePathSource(map.current, displayedCoordinates, inPipMode);

      map.current.setCenter(midpoint);
    }
  }, [
    currentPosition,
    prevPosition,
    firstDataReceived,
    mapLoaded,
    cumulativePath,
    headingDeg,
    targetZoomRef,
    createdTimeRef,
    inPipMode,
    map,
    flyToEnabledRef
  ]);

  return markerRef;
}
